import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import Footer from './Footer';
import {
    LuUser,
    LuMail,
    LuPhone,
    LuLock,
    LuShield,
    LuCrown,
    LuCalendar,
    LuBell,
    LuTrash2,
    LuCheck,
    LuCircleAlert
} from 'react-icons/lu';

function Profile() {
    const { t } = useLanguage();
    const {
        user,
        updateProfile,
        changePassword,
        deleteAccount: deleteAccountRequest
    } = useAuth();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('personal');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    // Form states
    const [name, setName] = useState(user?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [phone, setPhone] = useState(user?.phone || '');

    // Password states
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    // Preferences
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [pushNotifications, setPushNotifications] = useState(true);
    const [weeklyReport, setWeeklyReport] = useState(false);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            await updateProfile({ name, email, phone });
            setSuccess(t('profile.saved'));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword !== confirmNewPassword) {
            setError(t('auth.passwordMismatch'));
            return;
        }

        if (newPassword.length < 6) {
            setError(t('auth.passwordTooShort'));
            return;
        }

        setLoading(true);
        try {
            await changePassword(currentPassword, newPassword);
            setSuccess(t('profile.saved'));
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (window.confirm(t('profile.deleteWarning'))) {
            const password = window.prompt(
                t('profile.currentPassword') || 'Current password'
            );

            if (!password) return;

            try {
                await deleteAccountRequest(password);
                navigate('/login', { replace: true });
            } catch (err) {
                setError(err.message);
            }
        }
    };

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    const subscriptionStatus = user.subscription?.status;

    const tabs = [
        { id: 'personal', label: t('profile.personalInfo'), icon: LuUser },
        { id: 'security', label: t('profile.security'), icon: LuShield },
        { id: 'subscription', label: t('profile.subscription'), icon: LuCrown },
        { id: 'preferences', label: t('profile.preferences'), icon: LuBell }
    ];

    return (
        <div className="app-container">
            <Navbar />

            <main className="main-content">
                <div className="profile-page">
                    <div className="profile-header">
                        <div className="profile-avatar">
                            <LuUser />
                        </div>
                        <div className="profile-info">
                            <h1>{user.name}</h1>
                            <p>{user.email}</p>
                            <div className="profile-meta">
                                <span>
                                    <LuCalendar />
                                    {t('profile.memberSince')}: {new Date(user.createdAt || Date.now()).toLocaleDateString()}
                                </span>
                                {subscriptionStatus === 'premium' && (
                                    <span className="premium-badge">
                                        <LuCrown /> Premium
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="profile-content">
                        {/* Sidebar Tabs */}
                        <div className="profile-sidebar">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <tab.icon />
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Content */}
                        <div className="profile-main">
                            {success && (
                                <div className="alert alert-success">
                                    <LuCheck /> {success}
                                </div>
                            )}
                            {error && (
                                <div className="alert alert-error">
                                    <LuCircleAlert /> {error}
                                </div>
                            )}

                            {/* Personal Info Tab */}
                            {activeTab === 'personal' && (
                                <div className="profile-section">
                                    <h2>{t('profile.personalInfo')}</h2>
                                    <form onSubmit={handleUpdateProfile}>
                                        <div className="form-group">
                                            <label>{t('profile.name')}</label>
                                            <div className="input-with-icon">
                                                <LuUser className="input-icon" />
                                                <input
                                                    type="text"
                                                    className="form-input has-icon"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t('profile.email')}</label>
                                            <div className="input-with-icon">
                                                <LuMail className="input-icon" />
                                                <input
                                                    type="email"
                                                    className="form-input has-icon"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t('profile.phone')}</label>
                                            <div className="input-with-icon">
                                                <LuPhone className="input-icon" />
                                                <input
                                                    type="tel"
                                                    className="form-input has-icon"
                                                    value={phone}
                                                    onChange={(e) => setPhone(e.target.value)}
                                                    placeholder="+90 555 555 55 55"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? t('loading.loading') : t('profile.updateProfile')}
                                        </button>
                                    </form>
                                </div>
                            )}

                            {/* Security Tab */}
                            {activeTab === 'security' && (
                                <div className="profile-section">
                                    <h2>{t('profile.changePassword')}</h2>
                                    <form onSubmit={handleChangePassword}>
                                        <div className="form-group">
                                            <label>{t('profile.currentPassword')}</label>
                                            <div className="input-with-icon">
                                                <LuLock className="input-icon" />
                                                <input
                                                    type="password"
                                                    className="form-input has-icon"
                                                    value={currentPassword}
                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t('profile.newPassword')}</label>
                                            <div className="input-with-icon">
                                                <LuLock className="input-icon" />
                                                <input
                                                    type="password"
                                                    className="form-input has-icon"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    required
                                                    minLength={6}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>{t('profile.confirmNewPassword')}</label>
                                            <div className="input-with-icon">
                                                <LuLock className="input-icon" />
                                                <input
                                                    type="password"
                                                    className="form-input has-icon"
                                                    value={confirmNewPassword}
                                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? t('loading.loading') : t('profile.changePassword')}
                                        </button>
                                    </form>

                                    <div className="security-section">
                                        <h3>{t('profile.twoFactor')}</h3>
                                        <p>{t('profile.twoFactorDescription')}</p>
                                        <button className="btn btn-secondary">
                                            {t('profile.enable')}
                                        </button>
                                    </div>

                                    <div className="danger-zone">
                                        <h3>{t('profile.deleteAccount')}</h3>
                                        <p>{t('profile.deleteWarning')}</p>
                                        <button
                                            className="btn btn-danger"
                                            onClick={handleDeleteAccount}
                                        >
                                            <LuTrash2 /> {t('profile.deleteAccount')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Subscription Tab */}
                            {activeTab === 'subscription' && (
                                <div className="profile-section">
                                    <h2>{t('profile.subscription')}</h2>
                                    <div className="subscription-card">
                                        <div className="subscription-plan">
                                            <div className="plan-icon">
                                                {subscriptionStatus === 'premium' ? <LuCrown /> : <LuUser />}
                                            </div>
                                            <div className="plan-details">
                                                <h3>
                                                    {subscriptionStatus === 'premium'
                                                        ? t('subscription.premium')
                                                        : t('subscription.free')}
                                                </h3>
                                                {subscriptionStatus === 'trial' && (
                                                    <p className="trial-info">
                                                        {user.subscription?.remainingDays || 0} {t('subscription.trialDays')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {subscriptionStatus !== 'premium' && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => navigate('/pricing')}
                                            >
                                                {t('subscription.upgrade')}
                                            </button>
                                        )}
                                        {subscriptionStatus === 'premium' && (
                                            <button className="btn btn-secondary">
                                                {t('subscription.manage')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Preferences Tab */}
                            {activeTab === 'preferences' && (
                                <div className="profile-section">
                                    <h2>{t('profile.preferences')}</h2>

                                    <div className="preference-group">
                                        <h3>{t('profile.notifications')}</h3>
                                        <div className="preference-item">
                                            <div className="preference-info">
                                                <LuMail />
                                                <div>
                                                    <span>{t('profile.emailNotifications')}</span>
                                                </div>
                                            </div>
                                            <label className="toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={emailNotifications}
                                                    onChange={(e) => setEmailNotifications(e.target.checked)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <div className="preference-item">
                                            <div className="preference-info">
                                                <LuBell />
                                                <div>
                                                    <span>{t('profile.pushNotifications')}</span>
                                                </div>
                                            </div>
                                            <label className="toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={pushNotifications}
                                                    onChange={(e) => setPushNotifications(e.target.checked)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                        <div className="preference-item">
                                            <div className="preference-info">
                                                <LuCalendar />
                                                <div>
                                                    <span>{t('profile.weeklyReport')}</span>
                                                </div>
                                            </div>
                                            <label className="toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={weeklyReport}
                                                    onChange={(e) => setWeeklyReport(e.target.checked)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default Profile;
